import { Logger, LogSender } from '../../../logger';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import { StreamingEventBus } from '../../abstract/StreamingEventBus';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingSubscription, {
  StreamingSubscriptionEventHandler,
} from '../../abstract/StreamingSubscription';
import {
  getMarketDataHeader,
  getMarketDataSubscribeRequest,
  MarketDataRequestPayload,
  MarketDataSnapshotResponse,
  MarketDataSnapshotResponseRaw,
  MarketDataSubscribeRequest,
  MarketDataSubscribeResponse,
  MarketDataUpdateResponse,
  MarketDataUpdateResponseRaw,
} from './Messages';

export type MarketDataOnSubscribe = (
  msg: MarketDataSubscribeResponse,
  err: StreamingError
) => void;
export type MarketDataOnSnapshot = (
  msg: MarketDataSnapshotResponse,
  err: StreamingError
) => void;
export type MarketDataOnUpdate = (
  msg: MarketDataUpdateResponse,
  err: StreamingError
) => void;

export default class MarketDataSubscription extends StreamingSubscription<
  MarketDataRequestPayload,
  MarketDataSubscribeRequest
> {
  // Subscribed symbol
  private _symbol: string;
  private _tenor: string;
  private _settlDate: string;

  public get subscribed(): MarketDataRequestPayload {
    return {
      symbol: this._symbol,
      tenor: this._tenor,
      settlDate: this._settlDate,
    };
  }
  public get SubscribeRequest(): MarketDataSubscribeRequest {
    return getMarketDataSubscribeRequest(this.id, this.subscribed);
  }

  public readonly on: {
    [DoryMessageType.MARKETDATAREQUEST]: StreamingSubscriptionEventHandler<MarketDataSubscribeResponse>;
    [DoryMessageType.MARKETDATASNAPSHOT]: StreamingSubscriptionEventHandler<MarketDataSnapshotResponse>;
    [DoryMessageType.MARKETDATAUPDATE]: StreamingSubscriptionEventHandler<MarketDataUpdateResponse>;
  } = {
    /**
     * The handler for "subscribe" has to be called when:
     * - a message of with messageType "subscribe" is received and channelID equal to this.id
     *
     * The handler is stopped when:
     * - it's called
     *
     * Attach an error object if:
     * - payload.subscription.failed !== 0
     */
    [DoryMessageType.MARKETDATAREQUEST]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.MARKETDATAREQUEST].isAttached = true;
        this.on[DoryMessageType.MARKETDATAREQUEST].detach =
          this.eventBus.once<MarketDataSubscribeResponse>(
            DoryServiceType.MARKETDATA,
            DoryMessageType.MARKETDATAREQUESTFAILED,
            (data) => {
              let err: StreamingError = undefined;
              if (data.payload.status === 400) {
                err = new StreamingError(
                  'Streaming.MarketData.MarketDataSubscription.onSubscribe',
                  StreamingErrorCodes.INVALID_RIC,
                  `Failed to subscribe symbol  "${this._symbol}".`
                );
              }

              this.on[DoryMessageType.MARKETDATAREQUEST].handler(data, err);
              this.on[DoryMessageType.MARKETDATAREQUEST].isAttached = false;
              this.stopTimeout();
            },
            (data) => data.header.channelID === this.id
          );
      },
    },
    /**
     * The handler for "snapshot" has to be called when:
     * - a message of with messageType "snapshot" is received that's not empty and either:
     *   - channelID equal to this.id is received or
     *   - it contains fids(bid, ask) and the subscribed symbol
     *
     * The handler is stopped when:
     * - snapshots for requested symbol have arrived (this.pending.length === 0)
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.MARKETDATASNAPSHOT]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.MARKETDATASNAPSHOT].isAttached = true;
        this.on[DoryMessageType.MARKETDATASNAPSHOT].detach =
          this.eventBus.on<MarketDataSnapshotResponseRaw>(
            DoryServiceType.MARKETDATA,
            DoryMessageType.MARKETDATASNAPSHOT,
            (data) => {
              Logger.silly(LogSender.STREAMING_MARKETDATA, 'Snapshot received');
              // Send snapshot if payload ric matched subscription ric
              if (this._symbol === data.payload.symbol) {
                Logger.silly(
                  LogSender.STREAMING_MARKETDATA,
                  'Snapshot matches subscription'
                );
                const index = this.pending.indexOf(data.payload.symbol);

                if (index !== -1) {
                  this.on[DoryMessageType.MARKETDATASNAPSHOT].handler(
                    this.parseResponse(data),
                    undefined
                  );
                  this.pending.splice(index, 1);
                  Logger.silly(
                    LogSender.STREAMING_MARKETDATA,
                    'Snapshot: removed ric from pending'
                  );
                }
                if (!this.isPending) {
                  this.on[DoryMessageType.MARKETDATAREQUEST].handler(
                    {
                      header: getMarketDataHeader(
                        DoryMessageType.MARKETDATAREQUEST
                      ),
                      payload: {
                        status: 200,
                        message: 'Successfully subscribed.',
                        messageType: '',
                      },
                    },
                    undefined
                  );
                  this.on[DoryMessageType.MARKETDATAREQUEST].isAttached = false;
                  this.stopTimeout();

                  this.on[DoryMessageType.MARKETDATASNAPSHOT]?.detach();
                  this.on[DoryMessageType.MARKETDATASNAPSHOT].detach =
                    undefined;
                  this.on[DoryMessageType.MARKETDATASNAPSHOT].isAttached =
                    false;
                  Logger.silly(
                    LogSender.STREAMING_MARKETDATA,
                    'Snapshot handler stopped'
                  );
                }
              }
            }
          );
      },
    },
    /**
     * The handler for "update" has to be called when:
     * - a message of with messageType "update" is received that contains subscribed symbol
     *
     * The handler is stopped when:
     * - the subscription is stopped
     *
     * Attach an error object if:
     * - never
     */
    [DoryMessageType.MARKETDATAUPDATE]: {
      handler: undefined,
      isAttached: false,
      attach: () => {
        this.on[DoryMessageType.MARKETDATAUPDATE].isAttached = true;
        this.on[DoryMessageType.MARKETDATAUPDATE].detach =
          this.eventBus.on<MarketDataUpdateResponseRaw>(
            DoryServiceType.MARKETDATA,
            DoryMessageType.MARKETDATAUPDATE,
            (data) => {
              // Check if message ric matches ric, payload[0] is hardcoded because the protocol defines this
              // field as array with a constant length = 1
              if (this._symbol.indexOf(data.payload.symbol) !== -1) {
                this.on[DoryMessageType.MARKETDATAUPDATE].handler(
                  this.parseResponse(data),
                  undefined
                );
              }
            }
          );
      },
    },
  };

  constructor(
    id: string,
    eventBus: StreamingEventBus,
    ric: string,
    tenor: string,
    settlDate: string,
    onSubscribe: MarketDataOnSubscribe,
    onSnapshot: MarketDataOnSnapshot,
    onUpdate: MarketDataOnUpdate
  ) {
    super(id, eventBus);

    this._symbol = ric;
    this._settlDate = settlDate;
    this._tenor = tenor;

    this.on[DoryMessageType.MARKETDATAREQUEST].handler = onSubscribe;
    this.on[DoryMessageType.MARKETDATASNAPSHOT].handler = onSnapshot;
    this.on[DoryMessageType.MARKETDATAUPDATE].handler = onUpdate;
  }

  public start(timeout: number): void {
    // for MarketData there is always one symbol/subscription
    this.pending = [this._symbol];
    // Attach all event listeners
    this.on[DoryMessageType.MARKETDATAREQUEST].attach();
    this.on[DoryMessageType.MARKETDATASNAPSHOT].attach();
    this.on[DoryMessageType.MARKETDATAUPDATE].attach();
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.MarketData.MarketDataSubscription.start',
      this.on[DoryMessageType.MARKETDATAREQUEST].handler
    );
  }
  public restart(timeout: number, pending: string[] = [this._symbol]): void {
    this.pending = [...pending];
    // Attach all event listeners that might be detached
    if (!this.on[DoryMessageType.MARKETDATAREQUEST].isAttached) {
      this.on[DoryMessageType.MARKETDATAREQUEST].attach();
    }
    if (!this.on[DoryMessageType.MARKETDATASNAPSHOT].isAttached) {
      this.on[DoryMessageType.MARKETDATASNAPSHOT].attach();
    }
    // Start pending timeout
    this.startTimeout(
      timeout,
      'Streaming.MarketData.MarketDataSubscription.restart',
      this.on[DoryMessageType.MARKETDATAREQUEST].handler
    );
  }
  public stop(): void {
    // Stop all event listeners
    if (this.on[DoryMessageType.MARKETDATAREQUEST].detach) {
      this.on[DoryMessageType.MARKETDATAREQUEST].detach();
      Logger.silly(
        LogSender.STREAMING_MARKETDATA,
        'Detached Subscribe listener'
      );
    }
    if (this.on[DoryMessageType.MARKETDATASNAPSHOT].detach) {
      this.on[DoryMessageType.MARKETDATASNAPSHOT].detach();
      Logger.silly(
        LogSender.STREAMING_MARKETDATA,
        'Detached Snapshot listener'
      );
    }
    if (this.on[DoryMessageType.MARKETDATAUPDATE].detach) {
      this.on[DoryMessageType.MARKETDATAUPDATE].detach();
      Logger.silly(LogSender.STREAMING_MARKETDATA, 'Detached Update listener');
    }
    // Stop pending timeout
    this.stopTimeout();
  }

  /**
   * Parse the message sent from the WebSocket into a an Object
   *
   * @param response Raw server response
   * @returns Parsed response
   */
  private parseResponse(
    response: MarketDataSnapshotResponseRaw | MarketDataUpdateResponseRaw
  ): MarketDataSnapshotResponse | MarketDataUpdateResponse {
    const data: {
      [key: string]: string;
    } = {};
    for (let i = 0; i < response.payload.fids.length; i++) {
      data[response.payload.fids[i]] = response.payload.values[i];
    }

    return {
      header: response.header,
      payload: {
        symbol: response.payload.symbol,
        data: data,
      },
    };
  }
}
